import { useQuery } from '@tanstack/react-query';
import { useSubscription } from '../subscriptions/queries';
import QualAPI from 'utils/QualAPI';

// Define query keys for upgrades
export const upgradeKeys = {
  all: ['upgrades'],
  details: (subscriptionId) => ['upgrades', 'details', subscriptionId],
};

/**
 * Hook to fetch available upgrades
 * @returns {Object} The query result containing upgrades data
 */
export function useUpgrades() {
  return useQuery({
    queryKey: upgradeKeys.all,
    queryFn: () => QualAPI.fetchUpgrades().then(response => response.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch upgrade information with calculated values
 * @returns {Object} The query result with upgrade info including pricing calculations
 */
export function useUpgradeInfo() {
  const { data: subscription } = useSubscription();
  const { data: upgradesData, ...upgradeQuery } = useUpgrades();
  
  // Extract the first upgrade plan (matches existing behavior)
  const upgradePlan = upgradesData?.plans?.[0] || null;
  const currentPlan = subscription?.plan || {};
  const quantity = subscription?.quantity || 1;
  
  // Calculate upgrade pricing information
  let upgradeInfo = {
    plan_id: upgradePlan?.id || null,
    upgrade_amount_total: null,
    upgrade_per_person: null,
    upgrade_savings: null,
  };

  if (upgradePlan && subscription) {
    const currentAmount = parseFloat(currentPlan.amount) || 0;
    const upgradeAmount = parseFloat(upgradePlan.amount) || 0;
    
    // Calculate per person cost (amounts are in cents, convert to dollars)
    upgradeInfo.upgrade_per_person = upgradeAmount / 100;
    
    // Calculate total cost
    upgradeInfo.upgrade_amount_total = (upgradeAmount * quantity) / 100;
    
    // Calculate savings (only for annual plans)
    // If current is monthly and upgrade is annual, calculate yearly savings
    if (currentPlan.interval === 'month' && upgradePlan.interval === 'year') {
      const yearlyCurrentCost = (currentAmount * quantity * 12) / 100;
      const yearlyUpgradeCost = (upgradeAmount * quantity) / 100;
      upgradeInfo.upgrade_savings = Math.max(0, yearlyCurrentCost - yearlyUpgradeCost);
    }
    // If current is annual and upgrade is monthly, no savings
    else if (currentPlan.interval === 'year' && upgradePlan.interval === 'month') {
      upgradeInfo.upgrade_savings = 0;
    }
  }
  
  return {
    ...upgradeQuery,
    data: upgradeInfo,
  };
}